import { ReactNode } from 'react';
import { ListBox, ListBoxItem, type Key } from 'react-aria-components';
import { Check } from 'lucide-react';
import { cx } from './cx';
import { Icon } from './Icon';
import { typography } from './Text';

export interface FilterListOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

export interface FilterListProps {
  options: FilterListOption[];
  value: string;
  onChange: (value: string) => void;
  'aria-label'?: string;
  className?: string;
}

// Negative inline margin lets the hover/selected background bleed into the
// popover gutter (menu style) while the label text still lines up with the
// other controls in a ColumnFilter panel.
const item =
  `flex cursor-pointer items-center justify-between gap-3 rounded-md -mx-2 px-2 py-1.5 ${typography.body} text-fg-secondary outline-none ` +
  'data-[focused]:bg-muted-strong data-[selected]:bg-muted data-[selected]:text-fg data-[selected]:font-medium';

/**
 * Compact single-select option list — the popover-friendly counterpart to
 * FilterChips. Vertical rows that never overflow their container, with a brand
 * check on the selected row. Use inside a ColumnFilter or any dropdown panel;
 * for an inline toolbar use FilterChips instead.
 */
export const FilterList = ({
  options,
  value,
  onChange,
  className,
  ...rest
}: FilterListProps) => (
  <ListBox
    selectionMode="single"
    selectedKeys={value ? [value] : []}
    disallowEmptySelection
    onSelectionChange={keys => {
      const next = [...keys][0] as Key | undefined;
      if (next !== undefined) onChange(String(next));
    }}
    aria-label={rest['aria-label']}
    className={cx('flex flex-col outline-none', className)}
  >
    {options.map(option => (
      <ListBoxItem
        key={option.value}
        id={option.value}
        textValue={option.label}
        className={item}
      >
        {({ isSelected }) => (
          <>
            <span className="flex min-w-0 items-center gap-2">
              {option.icon}
              <span className="truncate">{option.label}</span>
            </span>
            {isSelected && (
              <Icon
                as={Check}
                size="sm"
                strokeWidth={2.5}
                className="shrink-0 text-brand-text"
              />
            )}
          </>
        )}
      </ListBoxItem>
    ))}
  </ListBox>
);
